import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { createAdminQaItem, updateAdminQaItem } from '@/lib/api/admin';

function emptyForm() {
  return {
    sourceId: '',
    section: '',
    question: '',
    answer: '',
    aliases: [],
    lockAfterAnswer: false,
  };
}

function fromItem(item) {
  if (!item) return emptyForm();
  return {
    sourceId: String(item?.metadata?.sourceId || '').trim(),
    section: String(item?.section || ''),
    question: String(item?.question || ''),
    answer: String(item?.answer || ''),
    aliases: Array.isArray(item?.aliases) ? [...item.aliases] : [],
    lockAfterAnswer: Boolean(item?.lockAfterAnswer),
  };
}

function QaItemEditorDialog({ open, item, onOpenChange, onSaved }) {
  const isEditing = Boolean(item?.id);
  const [form, setForm] = useState(() => fromItem(item));
  const [aliasDraft, setAliasDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(fromItem(item));
      setAliasDraft('');
      setError(null);
    }
  }, [open, item]);

  const isValid = useMemo(() => {
    const answer = form.answer.trim();
    if (!answer) return false;
    const hasQuestionOrAlias =
      form.question.trim().length > 0 || form.aliases.some((a) => String(a || '').trim().length > 0);
    return hasQuestionOrAlias;
  }, [form]);

  const setField = (name) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addAliases = (raw) => {
    if (!raw) return;
    const parts = String(raw)
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    setForm((current) => {
      const set = new Set(current.aliases.map((a) => a.trim()));
      const next = [...current.aliases];
      for (const p of parts) {
        if (!set.has(p)) {
          set.add(p);
          next.push(p);
        }
      }
      return { ...current, aliases: next };
    });
    setAliasDraft('');
  };

  const removeAlias = (idx) => {
    setForm((current) => ({
      ...current,
      aliases: current.aliases.filter((_, i) => i !== idx),
    }));
  };

  const handleAliasKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addAliases(aliasDraft);
    } else if (event.key === 'Backspace' && !aliasDraft && form.aliases.length > 0) {
      // Quick undo of the last alias chip.
      setForm((current) => ({
        ...current,
        aliases: current.aliases.slice(0, -1),
      }));
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!isValid || isSaving) return;
    setIsSaving(true);
    setError(null);
    const trimmedAliases = form.aliases.map((a) => a.trim()).filter(Boolean);
    const payload = {
      section: form.section.trim(),
      question: form.question.trim(),
      answer: form.answer.trim(),
      aliases: trimmedAliases,
      sourceId: form.sourceId.trim(),
      lockAfterAnswer: form.lockAfterAnswer,
    };
    try {
      if (isEditing) {
        await updateAdminQaItem({ id: item.id, ...payload });
        toast.success('Phrase updated');
      } else {
        await createAdminQaItem(payload);
        toast.success('Phrase created');
      }
      onSaved?.();
    } catch (err) {
      if (err?.status === 409) {
        setError(`Source ID is already used by item ${err?.payload?.conflictId || 'another row'}.`);
      } else {
        setError(err?.message || 'Save failed');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <form className="flex flex-col overflow-hidden" onSubmit={handleSubmit}>
          <DialogHeader className="shrink-0 space-y-1.5 px-6 pt-6">
            <DialogTitle>{isEditing ? 'Edit phrase' : 'New phrase'}</DialogTitle>
            <DialogDescription>
              A phrase pairs the triggers the bot listens for with the reply it sends. Add a trigger phrase (or at least
              one alternative trigger), plus the reply.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[min(58vh,520px)] min-h-0 w-full px-6">
            <div className="space-y-4 pb-4">
              <Field label="Source ID" hint="Matches `### <id>` in Markdown imports and the ID column in Excel. Leave blank for a UI-only item.">
                <Input
                  value={form.sourceId}
                  onChange={setField('sourceId')}
                  placeholder="e.g. lost-glasses-1"
                  className="font-mono text-sm"
                />
              </Field>

              <Field label="Category" hint="Optional grouping label, shown as a coloured tag.">
                <Input
                  value={form.section}
                  onChange={setField('section')}
                  placeholder="e.g. Spectacles"
                />
              </Field>

              <Field label="Trigger phrase" hint="What the patient says that should fire this reply — a statement or a question.">
                <Input
                  value={form.question}
                  onChange={setField('question')}
                  placeholder="e.g. i lost my glasses"
                />
              </Field>

              <Field label="Alternative triggers" hint="Other ways a patient might say it. Press Enter to add, or paste pipe-separated values.">
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-input bg-background px-2 py-2">
                  {form.aliases.map((alias, idx) => (
                    <Badge key={`${alias}-${idx}`} variant="secondary" className="gap-1 pl-2 pr-1">
                      {alias}
                      <button
                        type="button"
                        className="rounded p-0.5 opacity-60 hover:opacity-100"
                        onClick={() => removeAlias(idx)}
                        aria-label={`Remove alias ${alias}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <input
                    type="text"
                    value={aliasDraft}
                    onChange={(event) => setAliasDraft(event.target.value)}
                    onKeyDown={handleAliasKeyDown}
                    onBlur={() => addAliases(aliasDraft)}
                    placeholder={form.aliases.length === 0 ? 'i lost my spectacles | my glasses are missing' : ''}
                    className="min-w-[12ch] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </Field>

              <Field
                label="Bot reply"
                hint="The exact reply the patient receives on a confident match, including its call-to-action — the assistant lightly adapts it to context."
              >
                <textarea
                  value={form.answer}
                  onChange={setField('answer')}
                  rows={10}
                  className={cn(
                    'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                  placeholder="The reply text, including the specific call-to-action. Markdown allowed."
                />
              </Field>

              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-input bg-background px-3 py-2.5">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
                  checked={form.lockAfterAnswer}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, lockAfterAnswer: event.target.checked }))
                  }
                />
                <span className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Hand off to staff after replying</span>
                  <span className="text-xs text-muted-foreground">
                    After sending this reply, silently lock the conversation so a colleague takes over — the
                    patient sees only the reply above.
                  </span>
                </span>
              </label>

              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isSaving}>
              {isSaving && <Spinner className="mr-2 h-4 w-4" />}
              {isEditing ? 'Save changes' : 'Create phrase'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export default QaItemEditorDialog;
